import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import * as React from 'react';
import {
  AMBER,
  FONT_BODY,
  FONT_DISPLAY,
  FONT_MONO,
  glassCardSx,
  gridLayerSx,
  Reveal,
  SectionHeading,
  TEAL,
} from './LandingKit';

const tiers = [
  {
    title: 'Básico',
    fixedPrice: '2.990',
    price: '9,99',
    description: [
      'Saídas ilimitadas',
      'Suporte por email',
      'Torre de Controle'
    ],
    buttonText: 'Agendar demonstração',
    buttonVariant: 'outlined',
    buttonColor: 'primary',
    buttonLink: '/leads/new?source=landing-pricing-basic',
  },
  {
    title: 'Profissional',
    subheader: 'Recomendado',
    fixedPrice: '3.490',
    price: '14,99',
    description: [
      'Saídas ilimitadas',
      'Suporte por email',
      'Torre de Controle',
      'Analytics',
      'Mapa e Geolocalização'
    ],
    buttonText: 'Agendar demonstração',
    buttonVariant: 'contained',
    buttonColor: 'secondary',
    buttonLink: '/leads/new?source=landing-pricing-professional',
  },
  {
    title: 'Enterprise',
    fixedPrice: '5.690',
    price: '19,99',
    description: [
      'Saídas ilimitadas',
      'Suporte por email e WhatsApp',
      'Torre de Controle',
      'Analytics',
      'Mapa e Geolocalização',
      'Integração com outros sistemas',
      'Inteligência Artificial via WhatsApp',
      'Suporte dedicado'
    ],
    buttonText: 'Agendar demonstração',
    buttonVariant: 'outlined',
    buttonColor: 'primary',
    buttonLink: '/leads/new?source=landing-pricing-enterprise',
  },
];

// Solid teal gradient button (matches hero primary CTA).
const filledButtonSx = {
  px: 3.5,
  py: 1.3,
  borderRadius: 999,
  fontFamily: FONT_BODY,
  fontWeight: 700,
  textTransform: 'none',
  fontSize: '0.95rem',
  color: '#04110f',
  background: `linear-gradient(180deg, ${TEAL}, #00a594)`,
  boxShadow: `0 10px 30px ${TEAL}40`,
  transition: 'transform 0.15s ease, box-shadow 0.15s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 16px 40px ${TEAL}66`,
    background: `linear-gradient(180deg, ${TEAL}, #00a594)`,
  },
};

// Teal-outline button (matches hero secondary CTA).
const outlinedButtonSx = (theme) => ({
  px: 3,
  py: 1.3,
  borderRadius: 999,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '0.95rem',
  color: '#0a3f39',
  border: '1px solid',
  borderColor: `${TEAL}66`,
  backgroundColor: 'transparent',
  '&:hover': { borderColor: TEAL, backgroundColor: `${TEAL}10` },
  ...theme.applyStyles('dark', { color: '#cfeae5', borderColor: `${TEAL}55` }),
});

export default function Pricing() {
  return (
    <Box
      id="pricing"
      sx={(theme) => ({
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: '#eef3f2',
        ...theme.applyStyles('dark', { backgroundColor: '#05080b' }),
      })}
    >
      <Box aria-hidden sx={gridLayerSx} />
      <Box
        aria-hidden
        sx={(theme) => ({
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: `radial-gradient(48% 40% at 50% 0%, ${TEAL}1f, transparent 70%)`,
          ...theme.applyStyles('dark', {
            background: `radial-gradient(48% 40% at 50% 0%, ${TEAL}2e, transparent 70%)`,
          }),
        })}
      />
      <Container
        sx={{
          pt: { xs: 4, sm: 12 },
          pb: { xs: 8, sm: 16 },
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: 4, sm: 7 },
        }}
      >
        <SectionHeading
          overline="PLANOS"
          align="center"
          title="Preço que acompanha sua operação"
          subtitle="Uma mensalidade fixa mais uma taxa por saída de veículo registrada. Você paga conforme a operação roda, sem pacote ocioso."
        />
        <Grid
          container
          spacing={3}
          sx={{ alignItems: 'stretch', justifyContent: 'center', width: '100%' }}
        >
          {tiers.map((tier, index) => {
            const recommended = tier.title === 'Profissional';
            return (
              <Grid
                size={{ xs: 12, sm: tier.title === 'Enterprise' ? 12 : 6, md: 4 }}
                key={tier.title}
                sx={{ display: 'flex' }}
              >
                <Reveal delay={index * 0.08} sx={{ width: '100%', display: 'flex' }}>
                  <Box
                    sx={(theme) => ({
                      ...glassCardSx(theme),
                      position: 'relative',
                      p: 3,
                      width: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 2,
                      minHeight: 560,
                      ...(recommended && {
                        borderColor: `${TEAL}88`,
                        boxShadow: `0 0 0 1px ${TEAL}55, 0 24px 60px ${TEAL}26`,
                        '&:hover': {
                          transform: 'translateY(-4px)',
                          boxShadow: `0 0 0 1px ${TEAL}88, 0 30px 70px ${TEAL}3a`,
                          borderColor: TEAL,
                        },
                      }),
                      ...(recommended &&
                        theme.applyStyles('dark', {
                          borderColor: `${TEAL}88`,
                          boxShadow: `0 0 0 1px ${TEAL}66, 0 24px 60px ${TEAL}33`,
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: `0 0 0 1px ${TEAL}aa, 0 30px 70px ${TEAL}40`,
                            borderColor: TEAL,
                          },
                        })),
                    })}
                  >
                    <Box
                      sx={{
                        mb: 0.5,
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        gap: 2,
                      }}
                    >
                      <Typography
                        component="h3"
                        sx={(theme) => ({
                          fontFamily: FONT_MONO,
                          fontSize: 12,
                          letterSpacing: '0.16em',
                          textTransform: 'uppercase',
                          color: '#0a3f39',
                          ...theme.applyStyles('dark', { color: '#7df0e2' }),
                        })}
                      >
                        {tier.title}
                      </Typography>
                      {recommended && (
                        <Box
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.6,
                            px: 1.2,
                            py: 0.4,
                            borderRadius: 999,
                            background: `linear-gradient(180deg, ${TEAL}, #00a594)`,
                            color: '#04110f',
                            fontFamily: FONT_MONO,
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: '0.1em',
                            textTransform: 'uppercase',
                            boxShadow: `0 6px 18px ${TEAL}55`,
                          }}
                        >
                          <AutoAwesomeIcon sx={{ fontSize: 13 }} />
                          {tier.subheader}
                        </Box>
                      )}
                    </Box>

                    <Tooltip
                      title="Preço composto por uma mensalidade fixa e uma taxa por saída de veículo registrada no sistema"
                      placement="right"
                    >
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                        <Typography
                          component="span"
                          sx={(theme) => ({
                            fontFamily: FONT_DISPLAY,
                            fontWeight: 800,
                            letterSpacing: '-0.02em',
                            lineHeight: 1,
                            fontSize: 'clamp(2.2rem, 4vw, 3rem)',
                            color: '#071513',
                            ...theme.applyStyles('dark', { color: '#f2fbf9' }),
                          })}
                        >
                          R$ {tier.price}
                        </Typography>
                        <Typography
                          component="span"
                          sx={(theme) => ({
                            fontFamily: FONT_MONO,
                            fontSize: 11,
                            letterSpacing: '0.06em',
                            color: '#33514c',
                            ...theme.applyStyles('dark', { color: '#9fc4bd' }),
                          })}
                        >
                          por saída
                        </Typography>
                      </Box>
                    </Tooltip>
                    <Typography
                      sx={(theme) => ({
                        fontFamily: FONT_DISPLAY,
                        fontWeight: 700,
                        fontSize: '0.95rem',
                        letterSpacing: '-0.01em',
                        color: TEAL,
                        ...theme.applyStyles('dark', { color: '#7df0e2' }),
                      })}
                    >
                      R$ {tier.fixedPrice} / mês
                    </Typography>

                    <Box
                      sx={(theme) => ({
                        my: 2,
                        height: '1px',
                        background: `linear-gradient(90deg, ${TEAL}44, transparent)`,
                        ...theme.applyStyles('dark', {
                          background: `linear-gradient(90deg, ${TEAL}55, transparent)`,
                        }),
                      })}
                    />

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                      {tier.description.map((line) => (
                        <Box
                          key={line}
                          sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}
                        >
                          <CheckCircleRoundedIcon
                            sx={{ width: 19, color: recommended ? TEAL : AMBER }}
                          />
                          <Typography
                            component="span"
                            sx={(theme) => ({
                              fontFamily: FONT_BODY,
                              fontSize: '0.92rem',
                              color: '#33514c',
                              ...theme.applyStyles('dark', { color: '#cfeae5' }),
                            })}
                          >
                            {line}
                          </Typography>
                        </Box>
                      ))}
                    </Box>

                    <Box sx={{ mt: 'auto', pt: 3 }}>
                      <Button
                        fullWidth
                        as="a"
                        href={tier.buttonLink}
                        disableElevation
                        sx={recommended ? filledButtonSx : outlinedButtonSx}
                      >
                        {tier.buttonText}
                      </Button>
                    </Box>
                  </Box>
                </Reveal>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
}
